"use client";

// ── Statement | editor split for `/judge/[slug]` (`EX-PUB11`) ────────────────
// The same draggable-divider mechanics as
// `features/playground/components/PlaygroundLayout.tsx`'s main row — pointer
// drag with capture, clamped fraction, arrow-key resize, localStorage
// persistence — trimmed to one divider. The workspace has no secondary
// output/stdin split to nest, so reusing `PlaygroundLayout` itself would mean
// forcing a three-pane API onto a two-pane layout; this is the same pattern
// at the size the caller actually needs.

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import {
  loadWorkspaceSplitFraction,
  saveWorkspaceSplitFraction,
} from "../../lib/judge-workspace-split-storage";

const MIN_SHARE = 0.3;
const MAX_SHARE = 0.62;
const DEFAULT_SHARE = 0.46;
const STEP = 0.03;

function clamp(v: number): number {
  return Math.min(MAX_SHARE, Math.max(MIN_SHARE, v));
}

export function WorkspaceSplitShell({ left, right }: { left: ReactNode; right: ReactNode }) {
  const t = useTranslations("judge.problem");
  const [share, setShare] = useState(DEFAULT_SHARE);
  const rowRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0, share: DEFAULT_SHARE });
  const shareRef = useRef(DEFAULT_SHARE);

  useEffect(() => {
    const loaded = loadWorkspaceSplitFraction();
    shareRef.current = loaded;
    setShare(loaded);
  }, []);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!draggingRef.current || !rowRef.current) return;
      const rect = rowRef.current.getBoundingClientRect();
      const lg = window.matchMedia("(min-width: 1024px)").matches;
      const delta = lg
        ? (e.clientX - startRef.current.x) / rect.width
        : (e.clientY - startRef.current.y) / rect.height;
      const next = clamp(startRef.current.share + delta);
      shareRef.current = next;
      setShare(next);
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      saveWorkspaceSplitFraction(shareRef.current);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
    return () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
    };
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    draggingRef.current = true;
    startRef.current = { x: e.clientX, y: e.clientY, share: shareRef.current };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    let delta = 0;
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") delta = -STEP;
    else if (e.key === "ArrowRight" || e.key === "ArrowDown") delta = STEP;
    else return;
    e.preventDefault();
    const next = clamp(shareRef.current + delta);
    shareRef.current = next;
    setShare(next);
    saveWorkspaceSplitFraction(next);
  }, []);

  // `EX-EDR01`: a double-click puts the divider back where it started.
  const onDoubleClick = useCallback(() => {
    shareRef.current = DEFAULT_SHARE;
    setShare(DEFAULT_SHARE);
    saveWorkspaceSplitFraction(DEFAULT_SHARE);
  }, []);

  return (
    <div ref={rowRef} className="flex h-full min-h-0 min-w-0 flex-col lg:flex-row">
      <section
        className="flex min-h-[38vh] min-w-0 flex-col overflow-hidden lg:min-h-0"
        style={{ flex: `0 0 ${share * 100}%` }}
      >
        {left}
      </section>

      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label={t("splitStatementEditorLabel")}
        aria-valuemin={Math.round(MIN_SHARE * 100)}
        aria-valuemax={Math.round(MAX_SHARE * 100)}
        aria-valuenow={Math.round(share * 100)}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
        onDoubleClick={onDoubleClick}
        title={t("splitResetHint")}
        className="group relative flex h-2 shrink-0 cursor-row-resize touch-none flex-col items-center justify-center border-y border-[#E6E6E6] bg-[#F4F4F5] py-0.5 select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/50 lg:h-auto lg:w-1.5 lg:cursor-col-resize lg:border-x lg:border-y-0 lg:py-0"
      >
        <div
          className="h-0.5 w-10 rounded-full bg-[#B8B8B8] transition-colors duration-150 ease-out group-hover:bg-[#8A8A8A] group-active:bg-[#E94D1D] lg:h-10 lg:w-0.5"
          aria-hidden
        />
      </div>

      <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">{right}</section>
    </div>
  );
}
